// Internal runtime types — used by the library's own infrastructure.
// Not intended for direct use by application developers.

import type { AppID } from './projectManagement.ts';
import type { FileCategory, MeshType } from './data-interfaces.external.ts';

// ── Placeholders ──────────────────────────────────────────────────────────────

/** Placeholder for workpiece logic */
export interface Workpiece {}

// ── Viewport geometry ─────────────────────────────────────────────────────────

/** Flattened geometry arrays and rendering metadata for mesh serialization/restoration */
export interface VPData {
  vertices: Float32Array;
  normals: Float32Array;
  colors: Float32Array;
  drawGroups: Array<{ start: number; count: number; materialId: any }>;
  boundingBox: { min: number[]; max: number[]; center: number[] };
}

// ── File collection ───────────────────────────────────────────────────────────

export type FileIdx = number;

interface BaseFileData {
  /** ID of the app that generated the file */
  appSource: AppID;
  /** Unique index in the current session */
  fileIdx: FileIdx;
  /** Key used to link this file with external systems */
  fileKey: string;
  /** Display name of the file */
  fileName?: string;
  /** Raw file data */
  data: ArrayBuffer;
  /** Timestamp of creation */
  createdAt: Date;
  /** Optional index of a parent file (creates a tree structure) */
  parent?: FileIdx;
  /** Optional search/filter tags */
  tags?: string[];
  /** Whether to fetch the finalized configurator mesh. Default `true`. */
  finalize?: boolean;
  /** App-specific metadata storage */
  customData?: { [key: string]: any };
}

/** Categories that specifically require a 3D mesh (3mf/stl) for processing */
export const MESH_CATEGORIES = ['workpiece', 'outputPart' /* deprecated */, 'trcadMesh'] as const;
export type MeshCategory = (typeof MESH_CATEGORIES)[number];

/** Discriminated union: MeshCategory members are constrained to MeshType; all others accept any string fileType */
export type FileData =
  | (BaseFileData & { category: MeshCategory; fileType: MeshType })
  | (BaseFileData & { category: Exclude<FileCategory, MeshCategory>; fileType: string });

export interface FileExportArray extends Array<FileData> {}

/**
 * Branded type to ensure a string is never empty at the type level.
 * Requires an explicit cast (as ValidFilename) after runtime validation.
 */
export type ValidFilename = string & { readonly __brand: 'NonEmpty' };
